import type * as React from 'react'
import { useMemo } from 'react'

import type { UniqueIdentifier } from '@dnd-kit/core'
import { defaultAnimateLayoutChanges, useSortable } from '@dnd-kit/sortable'
import type { AnimateLayoutChanges } from '@dnd-kit/sortable'

import useDraggingCursor from '~publish/hooks/useDraggingCursor'

import { ColumnDragContext } from './hooks/useDraggableColumn'
import type { ColumnDragContextType } from './hooks/useDraggableColumn'

import styles from './SortableColumn.module.css'

import type { JSX } from 'react'

const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true })

type SortableColumnProps = {
  id: UniqueIdentifier
  disabled?: boolean
  dragOverlay?: boolean
  children: React.ReactNode
}

const SortableColumn = ({
  id,
  disabled = false,
  dragOverlay = false,
  children,
}: SortableColumnProps): JSX.Element => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    animateLayoutChanges,
    disabled,
    data: {
      type: 'container',
    },
  })

  useDraggingCursor(dragOverlay)

  const transformX = transform?.x
  const transformY = transform?.y
  const transformScaleX = transform?.scaleX
  const transformScaleY = transform?.scaleY
  const style = useMemo(
    () => ({
      transition,
      '--col-translate-x':
        transformX === undefined ? undefined : `${Math.round(transformX)}px`,
      '--col-translate-y':
        transformY === undefined ? undefined : `${Math.round(transformY)}px`,
      '--col-scale-x': transformScaleX ? `${transformScaleX}` : undefined,
      '--col-scale-y': transformScaleY ? `${transformScaleY}` : undefined,
    }),
    [transition, transformX, transformY, transformScaleX, transformScaleY],
  )

  const dragHandleProps = useMemo<ColumnDragContextType>(
    () => ({
      setActivatorNodeRef,
      setNodeRef,
      attributes,
      // Forward dnd-kit's own listeners object as-is, keys come from registered sensors.
      listeners,
      style,
      dragging: dragOverlay,
      draggable: !disabled,
    }),
    [
      setActivatorNodeRef,
      setNodeRef,
      attributes,
      listeners,
      style,
      dragOverlay,
      disabled,
    ],
  )

  if (!dragOverlay && isDragging) {
    return (
      <section
        className={styles.destination}
        style={style}
        id={String(id)}
        ref={disabled ? null : setNodeRef}
      />
    )
  }

  return (
    <ColumnDragContext.Provider value={dragHandleProps}>
      {children}
    </ColumnDragContext.Provider>
  )
}

export { SortableColumn }
